<template>
  <div v-if="layoutStore.breadcrumbsVisibility" :class="$style.breadcrumbs">
    <SCAOverflowContainer
      type="SCATag"
      :items="breadcrumbs"
      :link-component="RouterLink"
      style="--sca-overflow-container-gap: 0"
    />
  </div>
</template>
<script setup lang="ts">
import { SCAOverflowContainer } from "@scayla/components";
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import type { RouteLocationNormalizedLoaded, RouteRecordNormalized } from "vue-router";
import { RouterLink, useRoute } from "vue-router";

import { Routes } from "@/router";
import { useLayoutStore } from "@/store/layoutStore";

const { t: $t } = useI18n();

const route = useRoute();
const layoutStore = useLayoutStore();

// Some paths exist only to group children — such crumbs must stay labels only.
function isNavigable(record: RouteRecordNormalized): boolean {
  if (record.redirect) return true;
  return Object.keys(record.components ?? {}).length > 0;
}

/**
 * A route's `meta.breadcrumb`. A function is resolved per navigation, which is what lets a crumb name
 * something known only from the params; returning several labels lets one route contribute a whole
 * trail ("Bin" / the module it is showing) without a route record per crumb.
 */
type BreadcrumbMeta = string | string[] | ((route: RouteLocationNormalizedLoaded) => string | string[]);

function labelsOf(record: RouteRecordNormalized): string[] {
  const meta = record.meta.breadcrumb as BreadcrumbMeta;
  const resolved = typeof meta === "function" ? meta(route) : meta;
  return Array.isArray(resolved) ? resolved : [resolved];
}

const breadcrumbs = computed(() => {
  const bcList = route.matched.filter((r) => "breadcrumb" in r.meta);
  const isHomeCurrent = bcList.length === 0;
  const home = {
    label: $t("common.label.home"),
    to: isHomeCurrent ? undefined : { name: Routes.HOME },
    props: { selected: isHomeCurrent, variant: "breadcrumb", disabled: isHomeCurrent },
  };
  const crumbs = bcList.flatMap((bc) => {
    const labels = labelsOf(bc);
    return labels.map((label, i) => ({ record: bc, label, linksToRecord: i === labels.length - 1 }));
  });
  const items = crumbs.map((crumb, index) => {
    const isCurrent = index === crumbs.length - 1;
    const navigable = crumb.linksToRecord && isNavigable(crumb.record) && !isCurrent;
    return {
      label: crumb.label,
      to: navigable ? { name: crumb.record.name, params: {} } : undefined,
      props: { variant: "breadcrumb", selected: isCurrent, disabled: !navigable },
    };
  });
  return [home, ...items];
});
</script>
<style lang="scss" module>
.breadcrumbs {
  align-items: center;
  width: 100%;
  min-width: 0;
  margin-left: -4px;
}
</style>
